import React from "react";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import { Card, Table } from "antd";
import moment from "moment";
import { debounce } from "lodash";
import Swal from "sweetalert2";

// Components
import PageHeader from "../../components/PageHeader";
import QRCodeComponent from "../../components/QRCodeComponent";
import AppContext from "../../components/AppContext";
import { SearchMain } from "../../components/Search";
import { FooterDeposit } from "../../components/Footer";
import DataSourceMainSearch from "./components/dataSource-main-Search";
import { ModelPaymentVoucher } from "../../components/Model/add-model";
import {
  DetailModelCustomer,
  PayinDetailsModel,
  UpslipModel,
} from "../../components/Model/index-model";

// Helpers & Utils
import Apibank from "../../api/Apibank";
import Apiauth from "../../api/Apiauth";
import { removeSlashesFromPath } from "../../helpers/location/removeSlashesFromPath";
import { getRowClassName } from "../../helpers/getRowClassName";
import { scrollToAntSpinContainer, showToast, getRandomuserParams } from "../../utils";
import UpdateDataByRoute from "../../helpers/isUpdateDataByRoute";
import { ColumnsTab1 } from "../../components/Columns/colums-deposit";
import { getFuncReSend } from "../../utils/funcCallback";
import { getSearchCustomerData } from "../../utils/fetchData";

class Index extends React.Component {
  static contextType = AppContext;

  constructor(props) {
    super(props);
    this.state = {
      // UI States
      activeTabKey1: "tab1",
      isLoading: false,
      isSearching: false,
      isMobile: window.innerWidth <= 768,
      dropdownPlacement: "bottomRight",
      expanded: false,
      spinIcon: 0,

      // Modal Display States
      showDetailsModel: false,
      showPaymentModel: false,
      showModelUpslip: false,
      showQRCode: false,
      showDetailCustomer: false,
      showPaymentVoucherModel: false,

      // Data States
      isData: null,
      qrCodeData: null,
      customerData: null,
      isDataPayment: false,
      dataTable: [],
      searchData: [],
      startDateSearch: null,
      endDateSearch: null,

      // Pagination States
      tableParams: {
        pagination: { current: 1, pageSize: 10, total: 0 },
        filters: {},
        sortField: null,
        sortOrder: null,
      },
      tableParamsSearch: {
        pagination: { current: 1, pageSize: 10, total: 0 },
        filters: {},
        sortField: null,
        sortOrder: null,
      },
    };

    this.tableRef = React.createRef();

    // Bind Methods
    this.fetchData = this.fetchData.bind(this);
    this.getSearchDataTransaction = this.getSearchDataTransaction.bind(this);
    this.debouncedFetch = debounce(this.fetchData, 100);
    this.debouncedSearch = debounce(this.getSearchDataTransaction, 500);
  }

  // ---------------------------------------------------------
  // Lifecycle & Event Listeners
  // ---------------------------------------------------------
  componentDidMount() {
    window.scrollTo(0, 0);
    window.addEventListener("scroll", this.handleScroll);
    window.addEventListener("resize", this.handleResize);

    this.setState({ isLoading: true }, () => {
      this.debouncedFetch();
    });

    if (this.context.setFetchDepositDataCallback) {
      this.context.setFetchDepositDataCallback(this.handleReloadDeposit);
    }
  }

  componentWillUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
    window.removeEventListener("resize", this.handleResize);
    this.debouncedFetch.cancel();
    this.debouncedSearch.cancel();
  }

  handleResize = () => this.setState({ isMobile: window.innerWidth <= 768 });

  handleScroll = () => {
    const tableElement = this.tableRef.current;
    if (tableElement) {
      const isAtBottom = tableElement.getBoundingClientRect().bottom <= window.innerHeight;
      this.setState({ dropdownPlacement: isAtBottom ? "topLeft" : "bottomRight" });
    }
  };

  // ---------------------------------------------------------
  // Data Fetching Logic
  // ---------------------------------------------------------
  handleReloadDeposit = () => {
    this.setState({ isLoading: true }, () => this.debouncedFetch());
  };

  fetchData = async () => {
    const { tableParams } = this.state;
    const { setCountTransPayIn, setReloading } = this.context;
    const now = moment().format("YYYY-MM-DD");
    const params = getRandomuserParams(tableParams);

    const itemParam = {
      limit: params.limit,
      offset: params.page,
      sort: "id",
      ...params.filters,
      orderby: params.orderby,
      order: params.order,
      startDate: now,
      endDate: now,
    };

    try {
      const res = await Apibank.get_data_deposit(itemParam);
      if (res?.data?.success) {
        let dataArray = res?.data?.data_transactions?.data?.rows || [];
        let count = res?.data?.data_transactions?.data?.count || 0;

        this.setState((prevState) => ({
          dataTable: dataArray,
          isLoading: false,
          tableParams: {
            ...prevState.tableParams,
            pagination: { ...prevState.tableParams.pagination, total: count },
          },
        }));

        setCountTransPayIn(res?.data?.data_transactions?.data?.count);
        setReloading(false);
      } else {
        showToast("error", res?.message || "Failed to fetch data.");
        this.setState({ isLoading: false });
      }
    } catch (error) {
      this.setState({ dataTable: [], isLoading: false });
      showToast("error", "Error fetching data.");
    }
  };

  getSearchDataTransaction = async () => {
    const { tableParamsSearch } = this.state;
    const { startDate, endDate, status, type, searchText, typeSearch } =
      tableParamsSearch?.filters?.searchData || {};

    const params = getRandomuserParams(tableParamsSearch);
    const formatDate = (date) => (date && moment(date?.$d).isValid() ? moment(date?.$d).format("YYYY-MM-DD") : null);

    const postData = {
      status,
      type,
      search: searchText,
      typeSearch,
      startDate: formatDate(startDate),
      endDate: formatDate(endDate),
      limit: params.limit,
      page: params.page,
    };

    try {
      const res = await Apiauth.searchTransaction(postData);
      if (res.data.success) {
        this.setState((prevState) => ({
          searchData: res?.data?.data_transactions || [],
          isLoading: false,
          tableParamsSearch: {
            ...prevState.tableParamsSearch,
            pagination: { ...prevState.tableParamsSearch.pagination, total: res?.data?.totalItems || 0 },
          },
        }));
      } else {
        showToast("error", res.data?.message || "Failed to fetch search data.");
        this.setState({ searchData: [], isLoading: false });
      }
    } catch (error) {
      this.setState({ searchData: [], isLoading: false });
      showToast("error", "Error fetching search data.");
    }
  };

  // ---------------------------------------------------------
  // Handlers & Actions
  // ---------------------------------------------------------
  handleSearch = (value) => {
    const today = moment().format("YYYY-MM-DD");
    const startDate = value?.startDate && moment(value.startDate?.$d).isValid() ? moment(value.startDate?.$d).format("YYYY-MM-DD") : today;
    const endDate = value?.endDate && moment(value.endDate?.$d).isValid() ? moment(value.endDate?.$d).format("YYYY-MM-DD") : today;

    this.setState(
      (prevState) => ({
        isSearching: true,
        isLoading: true,
        startDateSearch: startDate,
        endDateSearch: endDate,
        tableParamsSearch: {
          ...prevState.tableParamsSearch,
          pagination: { ...prevState.tableParamsSearch.pagination, current: 1 },
          filters: { ...prevState.tableParamsSearch.filters, ref: value?.searchText, searchData: value },
        },
      }),
      () => {
        scrollToAntSpinContainer();
        this.getSearchDataTransaction();
      }
    );
  };

  handleReSend = async (item) => {
    if (item === "Not allowed") return alert("Not authorized.");
    const id = item?.id;
    if (!id) return showToast("error", "Id not found");

    this.setState({ isLoading: true, spinIcon: id });

    const result = await Swal.fire({
      title: "Do you want to Re Send?",
      text: "Press 'Confirm' to confirm the action",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: this.props.t("Confirm"),
      cancelButtonText: this.props.t("Cancel"),
    });

    if (result.isConfirmed) {
      const res = await getFuncReSend(id);
      if (res) this.debouncedFetch();
    }
    this.setState({ isLoading: false, spinIcon: 0 });
  };

  handleShowModelDetelCustomer = (item) => {
    if (!item?.bank) return alert(this.props.t("Invalid data."));
    const info = JSON.parse(item.bank);
    this.setState({ customerData: info }, () => {
      const searchParams = {
        search: info?.accountNo,
        typeSearch: "account_no",
        merchantId: item.merchantId,
      };
      this.onSearchCustomerData(searchParams);
    });
  };

  onSearchCustomerData = async (params) => {
    const res = await getSearchCustomerData(params);
    if (!res) return showToast("error", this.props.t("Customer data not found."));
    this.setState({ showDetailCustomer: true, isData: res });
  };

  // ---------------------------------------------------------
  // UI Helpers
  // ---------------------------------------------------------
  handleCloseModels = () => {
    this.setState({
      showDetailsModel: false,
      showPaymentModel: false,
      showModelUpslip: false,
      showPaymentVoucherModel: false,
      showDetailCustomer: false,
      isData: null,
      customerData: null,
      isDataPayment: false,
    });
  };

  handleCloseQrcode = () => {
    const { getDataBankAccount } = this.context;
    this.setState({ showQRCode: false, qrCodeData: null, showDetailsModel: false, showPaymentModel: false, isData: null }, () => {
      this.debouncedFetch();
      getDataBankAccount();
    });
  };

  handleIsSearching = (val) => this.setState({ isSearching: val });

  // ---------------------------------------------------------
  // Rendering
  // ---------------------------------------------------------
  render() {
    const { t, i18n, } = this.props;
    const { allBankAccountData, getDataBankAccount, activeKey1, nameMerchang, resAllDataMerchang, resProfileAdmin, isMobile } = this.context;
    const {
      activeTabKey1, showDetailsModel, isData, isLoading, tableParams, dataTable,
      qrCodeData, showQRCode, showModelUpslip, isSearching, searchData, tableParamsSearch,
      showPaymentVoucherModel, isDataPayment, startDateSearch, endDateSearch, showDetailCustomer,
      dropdownPlacement, expanded
    } = this.state;

    const languageFormat = i18n.language === "lao" ? "lo" : i18n.language;

    // Table Columns Definition
    const columns = ColumnsTab1(
      t,
      (key) => {
        if (!key) return showToast("error", t("Invalid data."));
        if (key === "Not allowed") return alert("Not authorized.");
        this.setState({ showDetailsModel: true, isData: key });
      },
      this.handleReSend,
      languageFormat,
      (item) => {
        if (!item?.bank) return alert("error: no data settleSlip.");
        this.setState({ isDataPayment: item, showPaymentVoucherModel: true });
      },
      dropdownPlacement,
      this.handleShowModelDetelCustomer,
      resProfileAdmin,
      expanded
    );

    const displayStartDate = isSearching ? startDateSearch : moment(tableParams.filters.startDate).format("YYYY-MM-DD");
    const displayEndDate = isSearching ? endDateSearch : moment(tableParams.filters.endDate).format("YYYY-MM-DD");

    return (
      <div style={{ flex: 1 }}>
        {/* Modals Section */}
        <PayinDetailsModel isMobile={isMobile} handleClose={this.handleCloseModels} allBankAccountData={allBankAccountData} isData={isData} show={showDetailsModel} type={activeTabKey1} t={t} nameMerchang={nameMerchang} />
        <ModelPaymentVoucher handleClose={this.handleCloseModels} isData={isDataPayment} show={showPaymentVoucherModel} title="deposit" resAllDataMerchang={resAllDataMerchang} nameMerchang={nameMerchang} t={t} />
        <UpslipModel handleClose={this.handleCloseModels} allBankAccountData={allBankAccountData} fetchData={this.debouncedFetch} isData={isData} show={showModelUpslip} type={activeTabKey1} t={t} />
        <DetailModelCustomer handleClose={this.handleCloseModels} isData={isData} showToast={showToast} show={showDetailCustomer} activeTabKey1={activeTabKey1} allBankAccountData={allBankAccountData} resAllDataMerchang={resAllDataMerchang} nameMerchang={nameMerchang} t={t} />
        <UpdateDataByRoute onUpdateAuto={() => this.debouncedFetch()} />

        <div className="container-fluid">
          <PageHeader
            HeaderText={t("Transaction - Payin")}
            Breadcrumb={[{ name: removeSlashesFromPath(window.location.pathname) }]}
          />

          {showQRCode ? (
            <div className="in">
              <QRCodeComponent
                qrCodeData={qrCodeData}
                handleCloseQrcode={this.handleCloseQrcode}
                handleShowPaymentModel={() => this.setState({ showPaymentModel: true, showQRCode: false })}
                getDataBankAccount={getDataBankAccount}
              />
            </div>
          ) : (
            <>
              {/* Search*/}
              <SearchMain t={t} type="deposit" onSearch={this.handleSearch} handleIsSearching={this.handleIsSearching} activeKey1={activeKey1} />
              <Card
                activeTabKey={activeTabKey1}
                onTabChange={(key) => {
                  this.handleIsSearching(false);
                  this.setState({ activeTabKey1: key, isLoading: true, tableParams: { ...tableParams, pagination: { ...tableParams.pagination, current: 1 } } }, () => this.debouncedFetch());
                }}
                tabList={[{
                  key: "tab1",
                  tab: (
                    <>
                      {!isSearching ? t("transaction_list_all") : t("transaction_list_search", { date: `${displayStartDate} - ${displayEndDate}` })}
                      {" : "} ({Intl.NumberFormat().format(isSearching ? tableParamsSearch.pagination.total : tableParams.pagination.total)} {t("items")})
                    </>
                  )
                }]}
              >

                {!isSearching ? (
                  <Table
                    className="col-lg-12"
                    columns={columns}
                    rowKey={(record) => record.id}
                    dataSource={dataTable}
                    loading={isLoading}
                    rowClassName={getRowClassName}
                    scroll={{ x: "max-content" }}
                    onChange={(p, f, s) => {
                      this.setState({ isLoading: true, tableParams: { ...this.state.tableParams, pagination: p, filters: f, sortField: s.field, sortOrder: s.order } }, () => {
                        scrollToAntSpinContainer();
                        this.debouncedFetch();
                      });
                    }}
                    pagination={{
                      ...tableParams.pagination,
                      showSizeChanger: true,
                      locale: { items_per_page: `/ ${t("paged")}` },
                      showTotal: (total, range) => (
                        <span>
                          <strong>{t("Page", { page: tableParams.pagination.current, totalPages: Math.ceil(total / tableParams.pagination.pageSize) })}</strong>
                          <span style={{ marginLeft: 10 }}>{t("Showing", { from: range[0], total: total, of: range[1] })}</span>
                        </span>
                      ),
                    }}
                  />
                ) : (
                  <DataSourceMainSearch
                    columns={columns}
                    dataSource={searchData}
                    isSearching={isSearching}
                    getRowClassName={getRowClassName}
                    loading={isLoading}
                    tableParams={tableParamsSearch}
                    handleIsSearching={this.handleIsSearching}
                    onChange={(p, f, s) => {
                      this.setState({ isSearching: true, isLoading: true, tableParamsSearch: { ...this.state.tableParamsSearch, pagination: p, filters: { ...this.state.tableParamsSearch.filters, ...f }, sortField: s.field, sortOrder: s.order } }, () => {
                        scrollToAntSpinContainer();
                        this.debouncedSearch();
                      });
                    }}
                  />
                )}
                <FooterDeposit t={t} />
              </Card>
            </>
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = ({ mailInboxReducer }) => ({ isInbox: mailInboxReducer.isInbox });
export default connect(mapStateToProps, {})(withTranslation()(Index));